import {Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID} from '@angular/core';
import {ImageService} from '@utils/services/image/image.service';
import {IMAGE_RESOLUTION} from '@api/models/image/image-resolution';
import {LeagueHttpService} from '@api/http/league/league-http.service';
import {SeasonHttpService} from '@api/http/season/season-http.service';
import {ITableData, TableType} from '@api/models/table/table';
import {League} from '@api/models/league/league';
import {LeagueType} from '@api/models/league/league-type';
import {ISeason} from '@api/models/season/season';
import {UrlService} from '@utils/services/url/url.service';
import {isPlatformBrowser} from '@angular/common';
import { ColorScheme } from '@lib/models/color-scheme/color-scheme';
import { LayoutService } from '@utils/services/layout/layout.service';

interface ITableGroup {
  name: string;
  tables: ITableData[];
}

@Component({
  selector: 'lib-league-table',
  templateUrl: './league-table.component.html',
  styleUrls: [ './league-table.component.scss' ],
})

export class LeagueTableComponent implements OnInit {

  public link: string;
  public name: string;
  public leagueLogoUrl: string;
  public loading = false;
  public tableTabs: ITableGroup[] = [];
  public selectedTabIndex = 0;
  public bundesligaSpecial = false;
  public expanded = false;
  public expandable = false;
  public availableSeasons: ISeason[];
  @Input() public league?: League;
  @Input() public selectedSeason?: ISeason;
  @Input() public filterForTeamId: string;
  @Input() public highlightTeamId: string;
  @Input() public rowLimit?: number;
  @Input() public smallTable = false;
  @Input() public singleTable = false;
  @Input() public openLinksInNewTab = false;
  @Input() public showHeader = true;
  @Input() public showAllGroupsTab = true;
  @Input() public showAllGroups = false;
  @Input() public showLeagueName = true;
  @Input() public showTabs = true;
  @Input() public showSeasonSelect = true;
  @Input() public condensed = false;
  @Input() public canExpand = true;
  @Input() public readableTableHeaders = false;
  @Input() public type = TableType.Total;
  @Input() public colorScheme: ColorScheme;
  @Output() private emptyTable = new EventEmitter();

  constructor( private leagueHttpService: LeagueHttpService,
               private seasonHttpService: SeasonHttpService,
               private layoutService: LayoutService,
               @Inject(PLATFORM_ID) private platformId: any ) {}

  public async ngOnInit(): Promise<void> {
    if (this.layoutService.darkMode && !this.colorScheme) {
      this.colorScheme = ColorScheme.Dark;
    }
    if (isPlatformBrowser(this.platformId) && window.innerWidth < 768) {
      if (this.canExpand) {
        this.expandable = true;
      }
      this.smallTable = true;
    }
    this.loading = true;
    if (!this.league || this.league.leagueType !== LeagueType.League) {
      const response = await this.leagueHttpService.getMainPageTable();
      if (!response || !response.success) {
        console.error('[League Table] Could not get main page table!');
        this.loading = false;
        return;
      }
      if (Array.isArray(response.table)) {
        this.league = await this.leagueHttpService.getLeague(response.leagueid);
      }
    }
    this.name = this.league.name;
    this.link = UrlService.getSubdomainUrl(this.league.readable_id);
    this.leagueLogoUrl = ImageService.getPictureUrl(this.league.pictures[0], IMAGE_RESOLUTION.LOW);
    await this.loadLeagueTableAndTeams(this.league);
    this.loading = false;
    if (this.filterForTeamId) {
      this.setTabIndexForTeam();
    }
  }

  public canShowTable(tabIndex: number, tableIndex: number): boolean {
    if (this.singleTable) {
      return tabIndex === 0 && tableIndex === 0;
    }
    return this.showAllGroups || this.selectedTabIndex === tabIndex || this.selectedTabIndex === -1
  }

  public onTabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  public onSeasonSelect(season: ISeason): void {
    if (!season) {
      return;
    }
    this.selectedSeason = season;
    this.getTableOfLeague(this.league);
  }

  private async loadLeagueTableAndTeams(league: League) {
    await this.getTableOfLeague(league);
  }

  private async getTableOfLeague(league: League): Promise<void> {
    this.tableTabs = [];
    if (league.table) {
      this.tableTabs.push({
        name: league.table.group_name,
        tables: [league.table]
      });
      return;
    }

    if (league.seasons?.length === 0) {
      console.warn('[League Table] Could not get season for league!', this.league._id);
      return;
    }
    this.availableSeasons = league.seasons;

    if (!this.selectedSeason) {
      this.selectedSeason = league.seasons[0];
    }
    if (!this.selectedSeason) {
      console.error('[League Table] Could not get season for league!', this.league._id);
      return;
    }
    const tables = await this.seasonHttpService.getSeasonTables(this.selectedSeason._id);
    let allTables;

    if (this.type === TableType.Home) {
      allTables = tables.home;
    } else if (this.type === TableType.Away) {
      allTables = tables.away;
    }
    allTables = allTables || tables.total;
    this.bundesligaSpecial = allTables.some((table) => table.group_name === 'Meistergruppe');
    if (this.bundesligaSpecial) {
      this.tableTabs = [
        { name: 'Finaldurchgang', tables: [] },
        { name: 'Grunddurchgang', tables: [] },
      ]
      allTables.forEach((table) => {
        if (table.group_name === 'Meistergruppe' || table.group_name === 'Qualifikationsgruppe') {
          this.tableTabs[0].tables.push(table as ITableData);
          return;
        }
        this.tableTabs[1].tables.push(table);
      });
      this.showTabs = !this.showHeader;
      return;
    }
    allTables.forEach((table, index) => {
      this.tableTabs.push({
        name: table.group_name,
        tables: [table]
      })
      if (table.table.findIndex((entry) => entry.teamname === 'Österreich') > -1) {
        this.selectedTabIndex = index;
      }
    });
    if (this.tableTabs.length === 0) {
      this.emptyTable.emit();
    }
  }

  private setTabIndexForTeam() {
    this.tableTabs.forEach((tab, index) => {
      tab.tables.forEach((table) => {
        if (table.table.some((entry) => entry.teamid === this.filterForTeamId)) {
          this.selectedTabIndex = index;
        }
      })
    })
  }

  // Show the more button, when there are hidden table entries or hidden table groups
  public showMoreButton(tableData: ITableData): boolean {
    if (this.singleTable && this.tableTabs.length > 0) {
      return true;
    }
    if (!this.rowLimit) {
      return false;
    }
    return tableData.table.length > this.rowLimit;
  }
}
